import { Alert, Button, Input, Pending } from '@cluey/cluey-components';
import update from 'immutability-helper';
import { first } from 'lodash';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';

import { api } from '../../../../../api';
import { Loader } from '../../../../../components/Loader';
import { TextArea, useTextAreaProps } from '../../../../../components/TextArea/TextArea';
import { useGetCancellationReasons } from '../../../../../hooks/queries/useEnrolmentQuery';
import { CancelReason } from '../../../../../types/hubInterfaces';
import type { InitialState } from '../../../../../types/hubTypes';
import { SchoolHolidayTerm } from '../../../../../types/hubTypes';
import { filterCancelReasons } from '../../../../../util/helper/cancelEnrolment';
import { getCurrentOrUpcomingSchoolHoliday } from '../../../../../v2/common/helpers/schoolHolidays';
import { CancelEnrolmentSummaryAlert } from '../../FormCancelEnrolment/CancelEnrolmentSummaryAlert';
import type { Props } from '../CancellationSteps';
import { ReasonCheckbox } from './ReasonCheckbox';

const filterSummerCancelReasons = (cancelReasons: Array<CancelReason>, isSummer: boolean) => {
  return !isSummer ? cancelReasons.filter((reason) => reason.label !== 'Cancelling for summer holiday') : cancelReasons;
};

const CancelReasons = (props: Props) => {
  const {
    onSubmitCancelRequest,
    cancelSelection: { selectedDate, rating, selectedReasons, other },
    setCancelSelection,
    selectedEnrolment,
  } = props;
  const { data: cancelReasons, isLoading: isLoadingReasons, error } = useGetCancellationReasons();
  const { isLoading: isSubmitting } = useSelector((state: InitialState) => state.ui.apiState.hubContactPage);

  const text = rating <= 3 ? "What didn't work for you?" : 'Why did you decide to cancel?';

  const { data: termsAndHolidays } = api.schoolTerm.holidays.useQuery();
  const currentOrUpcomingHoliday = getCurrentOrUpcomingSchoolHoliday((termsAndHolidays as SchoolHolidayTerm[]) || []);
  const isSummer = currentOrUpcomingHoliday?.type === 'Summer Holiday';
  const reasons = filterSummerCancelReasons(
    filterCancelReasons({ cancelReasons: first(cancelReasons)?.values, rating }),
    isSummer
  );

  const textAreaProps = useTextAreaProps({
    id: 'text-tell-us-something',
    labelText: 'Message (optional)',
    placeholder: "Anything else you'd like us to know?",
  });

  useEffect(() => {
    setCancelSelection((current) =>
      update(current, {
        message: {
          $set: textAreaProps.value,
        },
      })
    );
  }, [textAreaProps.value, setCancelSelection]);

  const getInputError = () => {
    const otherSelected = !!selectedReasons.find((res) => res.label === 'Other');
    if (otherSelected && !other.message) {
      return true;
    }
    return false;
  };

  const getCancelRequestDisabled = () => {
    if (!selectedDate || isSubmitting) {
      return true;
    }
    if (selectedReasons.length === 0) {
      return true;
    }
    if (getInputError()) {
      return true;
    }
    return false;
  };

  const onCheck = (reason: CancelReason) => {
    if (reason.label.toLowerCase() === 'other' && !!selectedReasons.find((res) => res.label === reason.label)) {
      onOtherChange('');
    }
    setCancelSelection((current) => {
      return update(current, {
        selectedReasons: {
          $apply: (selectedReasons: Array<CancelReason>) => {
            if (selectedReasons.find((res) => res.label === reason.label)) {
              return selectedReasons.filter((res) => res.label !== reason.label);
            }
            return [...selectedReasons, reason];
          },
        },
      });
    });
  };

  const onOtherChange = (text) => {
    setCancelSelection((current) =>
      update(current, {
        other: {
          message: {
            $set: text,
          },
        },
      })
    );
  };

  return (
    <Pending loading={isLoadingReasons} loader={<Loader />}>
      {!!error ? (
        <Alert type="error" body={`There was a problem retrieving the cancellation reasons. ${error}`} />
      ) : (
        <div className="w-full">
          <p className="text-lg font-bold text-blue-5">{text}</p>
          <p className="font-bold text-grey-5">Please select all that apply</p>
          <div className="my-4 h-px bg-grey-2"></div>
          <div className="flex flex-col gap-2.5">
            {reasons.map((reason) => {
              const checked = !!selectedReasons.find((res) => res.label === reason.label);
              return <ReasonCheckbox key={reason.label} checked={checked} onCheck={onCheck} reason={reason} />;
            })}
          </div>
          <Input
            error={getInputError()}
            className="mt-2.5 w-full"
            placeholder="Please specify"
            value={other.message}
            onChange={onOtherChange}
          />
          <TextArea {...textAreaProps} />
          <CancelEnrolmentSummaryAlert selectedEnrolment={selectedEnrolment} selectedDate={selectedDate} />
          <div className="mb-4 text-center lg:mb-8">
            <Button size="lg" disabled={getCancelRequestDisabled()} onClick={onSubmitCancelRequest}>
              Request cancellation
            </Button>
          </div>
        </div>
      )}
    </Pending>
  );
};

export default CancelReasons;
